import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EditModal, SummaryCard, TagInput, useEditModal } from '@nucleus-commerce/admin-kit/forms';
import {
  ProductFulfillmentMode,
  type Product,
  type ProductStatus,
  type ProductType,
} from '@nucleus-commerce/api-client/admin';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { ToggleGroup, ToggleGroupItem } from '@nucleus-commerce/ui/components/toggle-group';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { STATUS_TONE } from '@/shared/catalog/status-tone';

import { useProductScope } from '../hooks/use-product-scope';
import type { ProductFormInput } from '../schemas/product-form-schema';

import { SkuRootField, VendorField } from './basics-section';
import { CardEditAction, EditOnMasterLink, SharedChip } from './product-scope';
import { ProductTypeCombobox } from './product-type-combobox';

const STATUS_ORDER: ProductStatus[] = ['active', 'draft', 'archived'];
const TONE: Record<ProductStatus, string> = {
  active: STATUS_TONE.live,
  draft: STATUS_TONE.preparing,
  archived: STATUS_TONE.off,
};
const FULFILMENT_MODES = Object.values(ProductFulfillmentMode);

export interface ProductStatusCardProps {
  editing?: boolean;
}

/** Master status as a segmented control; the page's save bar saves it. */
export function ProductStatusCard({ editing = false }: ProductStatusCardProps) {
  const { t } = useTranslation(['products', 'common']);
  const { readOnly } = useProductScope();
  const form = useFormContext<ProductFormInput>();
  const status = useWatch({ control: form.control, name: 'status' });
  return (
    <SummaryCard
      title={t('products:sections.status')}
      badge={<SharedChip />}
      action={readOnly ? <EditOnMasterLink /> : null}
      tour={editing ? 'catalog.product-edit.status' : 'catalog.product-new.status-save'}
    >
      {readOnly ? (
        <span
          data-state="on"
          className={cn('w-fit rounded-md px-2 py-0.5 text-[13px] font-semibold', TONE[status])}
        >
          {t(`common:status.${status}`)}
        </span>
      ) : (
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem className="gap-2">
              <FormControl>
                <ToggleGroup
                  type="single"
                  role="radiogroup"
                  spacing={1}
                  aria-label={t('products:sections.status')}
                  value={field.value}
                  onValueChange={(next) => {
                    // A segmented control always holds a value: clicking the current one is a no-op.
                    if (next) field.onChange(next);
                  }}
                  className="bg-muted grid w-full grid-cols-3 gap-0.5 rounded-lg p-0.5"
                >
                  {STATUS_ORDER.map((value) => (
                    <ToggleGroupItem
                      key={value}
                      value={value}
                      className={cn(
                        'text-muted-foreground hover:text-foreground h-7 w-full rounded-md px-1 text-[13px] font-normal hover:bg-transparent data-[state=on]:font-semibold data-[state=on]:shadow-xs',
                        TONE[value],
                      )}
                    >
                      {t(`common:status.${value}`)}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <span className="text-muted-foreground text-xs">
        {t(`products:rail.status.hint.${status}`)}
      </span>
    </SummaryCard>
  );
}

export interface ProductFulfilmentSectionProps {
  editing?: boolean;
}

/** Fulfilment mode, production lead time and the design approval flag: a summary and a modal. */
export function ProductFulfilmentSection({ editing = false }: ProductFulfilmentSectionProps) {
  const { t } = useTranslation('products');
  const { readOnly } = useProductScope();
  const form = useFormContext<ProductFormInput>();
  const edit = useEditModal({
    form,
    fields: ['fulfillmentMode', 'leadTimeDaysMin', 'leadTimeDaysMax', 'requiresDesignApproval'],
  });
  const [mode, min, max, approval] = useWatch({
    control: form.control,
    name: ['fulfillmentMode', 'leadTimeDaysMin', 'leadTimeDaysMax', 'requiresDesignApproval'],
  });
  const days = productionDays(t, min.trim(), max.trim());
  const title = t('rail.fulfilment.title');

  return (
    <SummaryCard
      title={title}
      badge={<SharedChip />}
      action={
        <CardEditAction
          onEdit={edit.show}
          label={
            <>
              {t('scope.edit')} <span className="sr-only">{title}</span>
            </>
          }
        />
      }
      tour={editing ? 'catalog.product-edit.fulfilment' : undefined}
    >
      <span className="text-[13px]">
        {[t(`rail.fulfilment.mode.${mode}`), days].filter(Boolean).join(' · ')}
      </span>
      {approval ? (
        <span className="text-muted-foreground text-[13px]">{t('rail.fulfilment.cad')}</span>
      ) : null}
      {readOnly ? null : (
        <EditModal
          {...edit.modal}
          size="md"
          title={title}
          description={t('rail.fulfilment.description')}
        >
          <FulfilmentFields />
        </EditModal>
      )}
    </SummaryCard>
  );
}

/** "15–21 days", or as much of it as the product has; empty without either end. */
function productionDays(t: TFunction, min: string, max: string): string {
  if (min && max) {
    return min === max
      ? t('rail.fulfilment.days', { count: Number(min) })
      : t('rail.fulfilment.range', { min, max });
  }
  if (min) return t('rail.fulfilment.atLeast', { count: Number(min) });
  if (max) return t('rail.fulfilment.atMost', { count: Number(max) });
  return '';
}

function FulfilmentFields() {
  const { t } = useTranslation('products');
  const form = useFormContext<ProductFormInput>();
  const dayFields = [
    { name: 'leadTimeDaysMin', label: t('rail.fulfilment.from') },
    { name: 'leadTimeDaysMax', label: t('rail.fulfilment.to') },
  ] as const;
  return (
    <>
      <FormField
        control={form.control}
        name="fulfillmentMode"
        render={({ field }) => (
          <FormItem className="gap-2.5">
            <h3 id="fulfilment-mode" className="text-sm font-semibold">
              {t('rail.fulfilment.modeHeading')}
            </h3>
            <FormControl>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                aria-labelledby="fulfilment-mode"
                className="grid gap-2.5 sm:grid-cols-3"
              >
                {FULFILMENT_MODES.map((mode) => (
                  <label
                    key={mode}
                    className={cn(
                      'flex cursor-pointer flex-col gap-1 rounded-lg border px-3 py-2.5',
                      field.value === mode && 'border-primary bg-primary-tint',
                    )}
                  >
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <RadioGroupItem value={mode} />
                      {t(`rail.fulfilment.mode.${mode}`)}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {t(`rail.fulfilment.modeHint.${mode}`)}
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex flex-col gap-2.5">
        <div>
          <h3 className="text-sm font-semibold">{t('rail.fulfilment.productionHeading')}</h3>
          <p className="text-muted-foreground mt-0.5 text-[13px]">
            {t('rail.fulfilment.productionHint')}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {dayFields.map(({ name, label }) => (
            <FormField
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{label}</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input inputMode="numeric" className="pr-14" {...field} />
                    </FormControl>
                    <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-[13px]">
                      {t('rail.fulfilment.daysUnit')}
                    </span>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>
      </div>
      <FormField
        control={form.control}
        name="requiresDesignApproval"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start gap-3 border-t pt-4">
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                onBlur={field.onBlur}
                className="mt-0.5"
              />
            </FormControl>
            <div className="flex flex-col gap-0.5">
              <FormLabel>{t('fields.requiresDesignApproval')}</FormLabel>
              <FormDescription className="text-xs">{t('rail.fulfilment.cadHint')}</FormDescription>
            </div>
          </FormItem>
        )}
      />
    </>
  );
}

export interface ProductOrganisationSectionProps {
  productTypes: readonly ProductType[];
  /** The saved product on an edit page, for the style number's origin; null while creating. */
  saved?: Product | null;
  editing?: boolean;
  /** Under the shared fields; the store page's "In this store" group goes here. */
  children?: ReactNode;
}

/** Product type, vendor, style number, tags and search keywords, edited in place. */
export function ProductOrganisationSection({
  productTypes,
  saved = null,
  editing = false,
  children,
}: ProductOrganisationSectionProps) {
  const { t } = useTranslation('products');
  const { readOnly } = useProductScope();
  const form = useFormContext<ProductFormInput>();
  return (
    <SummaryCard
      title={t('sections.organisation')}
      badge={<SharedChip />}
      action={readOnly ? <EditOnMasterLink /> : null}
      tour={editing ? 'catalog.product-edit.organisation' : 'catalog.product-new.product-type'}
    >
      {readOnly ? (
        <OrganisationValues productTypes={productTypes} />
      ) : (
        <div className="flex flex-col gap-3">
          <FormField
            control={form.control}
            name="productTypeId"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('fields.productType')}</FormLabel>
                <FormControl>
                  <ProductTypeCombobox
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    productTypes={productTypes}
                    aria-invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <VendorField />
          <SkuRootField saved={saved} />
          <FormField
            control={form.control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.tags')}</FormLabel>
                <FormControl>
                  <TagInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder={t('fields.tagsPlaceholder')}
                    max={50}
                    removeLabel={(tag) => t('fields.removeTag', { tag })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="searchKeywords"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.searchKeywords')}</FormLabel>
                <FormControl>
                  <TagInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder={t('fields.searchKeywordsPlaceholder')}
                    max={50}
                    removeLabel={(tag) => t('fields.removeTag', { tag })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      {children}
    </SummaryCard>
  );
}

function ValueRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-muted-foreground text-xs">{label}</span>
      {children}
    </div>
  );
}

/** The organisation fields as plain values, for a store's page. */
function OrganisationValues({ productTypes }: { productTypes: readonly ProductType[] }) {
  const { t } = useTranslation('products');
  const { control } = useFormContext<ProductFormInput>();
  const [productTypeId, vendor, skuRoot, tags, keywords] = useWatch({
    control,
    name: ['productTypeId', 'vendor', 'skuRoot', 'tags', 'searchKeywords'],
  });
  const typeName = productTypes.find((type) => type.id === productTypeId)?.name;
  const notSet = <span className="text-muted-foreground">{t('rail.notSet')}</span>;
  return (
    <div className="flex flex-col gap-2">
      <ValueRow label={t('fields.productType')}>
        {productTypeId ? <span>{typeName ?? notSet}</span> : t('fields.noProductType')}
      </ValueRow>
      <ValueRow label={t('fields.vendor')}>{vendor ? <span>{vendor}</span> : notSet}</ValueRow>
      <ValueRow label={t('fields.skuRoot')}>
        {skuRoot ? <span className="font-mono text-[13px]">{skuRoot}</span> : notSet}
      </ValueRow>
      <ValueRow label={t('rail.organisation.tagsInAllStores')}>
        {tags.length > 0 ? (
          <span className="flex flex-wrap gap-1">
            {tags.map((tag) => (
              <span key={tag} className="bg-muted rounded-full px-2 text-xs">
                {tag}
              </span>
            ))}
          </span>
        ) : (
          notSet
        )}
      </ValueRow>
      <ValueRow label={t('fields.searchKeywords')}>
        {keywords.length > 0 ? <span className="text-[13px]">{keywords.join(', ')}</span> : notSet}
      </ValueRow>
    </div>
  );
}
